// Admin → Flow → Session Access: what to show for ONE customer, honestly (2026-10-03).
// The server keeps only the customer's LAST report (functions/flowSessionCore.js `report`,
// one health record per customer per Gmail). A report is a snapshot, so its age matters:
//   • fresh (≤ 15 min; the extension heartbeat is every 10 min) → the state is "now"
//   • older + last state was GOOD  → we no longer know → grey "Offline · last seen …"
//   • older + last state was a PROBLEM → still true until the customer comes back → keep it
// Only the record for the customer's CURRENT Gmail counts — a customer moved to another
// Gmail must not keep showing the status earned on the old one.
// Pure functions (no React, no Firestore) so the rule can be tested on its own.
export const SEGAR_MS = 15 * 60000;

// [label, tone] — tone: ok | warn | bad | idle.
export const HEALTH = {
    SESSION_DURABLE: ['Using Flow · stable', 'ok'],
    APPLICATION_AVAILABLE: ['Using Flow', 'ok'],
    SESSION_PROVISIONED: ['Just opened Flow', 'ok'],
    GENERATION_SUCCESS: ['Generated a video', 'ok'],
    SESSION_IDLE: ['Ready · not using Flow', 'ok'],
    AUTH_REQUIRED: ['Needs to re-open', 'warn'],
    SESSION_OUTDATED: ['Refreshing session', 'warn'],
    OTHER_SESSION: ['Other Gmail in this Chrome', 'warn'],
    ACCESS_REVOKED: ['Access removed', 'warn'],
    SESSION_PROVISION_FAILED: ["Couldn't open Flow", 'bad'],
    PAGE_LOAD_FAILED: ["Flow didn't load", 'bad'],
    GENERATION_FAILED: ['Video generate failed', 'bad'],
};

export const ago = (ts, now = Date.now()) => {
    if (!ts) return '';
    const s = Math.max(0, Math.round((now - ts) / 1000));
    if (s < 60) return 'just now';
    const m = Math.round(s / 60); if (m < 60) return `${m}m ago`;
    const h = Math.round(m / 60); if (h < 24) return `${h}h ago`;
    return `${Math.round(h / 24)}d ago`;
};

// health: every health record the overview returned · accountId: the customer's current Gmail
// · accounts: overview accounts (for the latest session version).
export function statusPelanggan({ uid, accountId, health = [], accounts = [], now = Date.now() }) {
    const milik = health.filter(h => h?.uid === uid);
    const h = milik.find(x => x.accountId === accountId) || null;
    const latest = accounts.find(a => a.accountId === accountId)?.currentSessionVersion || 0;
    if (!h || !Number.isFinite(h.timestamp)) {
        return { label: milik.length ? 'Not opened on this Gmail yet' : 'Not tried yet', tone: 'idle', when: '', online: false, using: false,
            version: null, latest, outdated: false };
    }
    const [label, tone] = HEALTH[h.event] || [String(h.event || 'unknown').replaceAll('_', ' ').toLowerCase(), 'idle'];
    const fresh = now - h.timestamp <= SEGAR_MS;
    const version = Number.isSafeInteger(h.sessionVersion) && h.sessionVersion > 0 ? h.sessionVersion : null;
    // using = has Flow open right now: a fresh GOOD report that is not the "browser alive,
    // Flow closed" heartbeat (SESSION_IDLE). This is what the "Online now" box counts.
    const using = fresh && tone === 'ok' && h.event !== 'SESSION_IDLE';
    const base = { when: ago(h.timestamp, now), online: fresh, using, version, latest, outdated: !!(version && latest && version < latest) };
    if (fresh || tone !== 'ok') return { ...base, label, tone };
    return { ...base, label: 'Offline', tone: 'idle', when: `last seen ${base.when}` };
}

// How fast "Remove" reaches this customer's browser (2026-10-09, functions/flowSessionIsyarat.js).
// isyarat = overview.isyarat: { [uid]: true | false }. true = at least one of the customer's browsers
// receives the server's signal → removed within seconds. false / missing = that browser has no
// signal (older extension, or push unavailable) → removed on its own next check (≤ 10 minutes
// while the browser is open, or the moment it opens Flow). Older server (no map) → show nothing.
export function labelIsyarat(isyarat, uid) {
    if (!isyarat || typeof isyarat !== 'object') return null;
    return isyarat[uid] === true ? { label: 'Remove: instant', tone: 'ok' } : { label: 'Remove: within 10 min', tone: 'idle' };
}

// "Online now" box on each Gmail card: customers using Flow right now / customers on that Gmail.
// total = access rows that are enabled, on this Gmail and not expired (same set as "granted").
// A snapshot of the last Refresh, like every status on this page.
export function kiraOnline({ accountId, access = [], health = [], accounts = [], now = Date.now() }) {
    const milik = access.filter(a => a?.enabled && a.accountId === accountId && !(a.endAt && a.endAt < now));
    const online = milik.filter(a => statusPelanggan({ uid: a.uid, accountId, health, accounts, now }).using).length;
    return { online, total: milik.length };
}
